<script setup>
import { Learner } from '@pfsc/core/models/learner';
import { getUserReadableStatus } from '@pfsc/core/models/user';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { redirectUtils } from '@pfsc/core/utils/redirect';
import { ref, watch } from 'vue';
import { useMobileDetection } from '@pfsc/core/ui-services/use-mobile-detection';
import { globalTranslate } from '../locale/app.i18n';
import UnassignedTableCheckbox from './unassigned-table-checkbox.vue';

// When show-select is true, the table will have this ID for the select checkbox row.
// However, if we use a custom #header to render the table, the select all rows checkbox will also be hidden
// Therefore, we need to manually handle selecting all rows again.
const SELECT_ROW_COLUMN_KEY = 'data-table-select';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  modelValue: {
    type: Array,
    required: false,
    default: () => [],
  },
  headers: {
    type: Array,
    required: true,
  },
  maximumLearnersSelected: {
    type: Number,
    default: null,
  },

  actionButtonTitle: {
    type: String,
    default: '',
  },

  hasStudentViewUrl: {
    type: Boolean,
    default: false,
  },

  showSelect: {
    type: Boolean,
    default: true,
  },

  shouldApplyClass: {
    type: Boolean,
    default: true,
  },

  selectStrategy: {

    /** @type {import("vue").PropType<"all" | "single" | "page">} */
    type: String,
    required: false,
    default: undefined,
  },

  disableHiddenLearners: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['on-remove', 'on-action', 'update:model-value']);

const authStore = useAuthStore();
const { isMobile } = useMobileDetection();

const isSelectAllRow = ref(false);

/**
 * Check if learner list include another list.
 * @param {Array} l1 Learner list 1.
 * @param {Array} l2 Learner list 2.
 * @returns {boolean}
 */
function checkIfLearnerListIncludeAnotherList(l1, l2) {
  const l1Ids = l1.map(l => l.id);
  const l2Ids = l2.map(c => c.id);

  return l2Ids.every(item => l1Ids.includes(item));
}

/**
 * Filter selectable item not included in a list.
 * @param {Learner[]} allItems All learners.
 * @param {Array<any>} exclude List to exclude.
 * @returns {Learner[]}
 */
function filterItemNotIncludedInAList(allItems, exclude) {
  const excludeIds = new Set(exclude.map(item => item.id));
  return allItems.filter(item => (
    (!props.disableHiddenLearners || item.isVisible)
    && !excludeIds.has(item.id)
  ));
}

/**
 * Toggle state of select all checkbox.
 * @param {Array} selectedRows Selected rows.
 */
function toggleSelectAllCheckbox(selectedRows) {
  const isIncludesAll = checkIfLearnerListIncludeAnotherList(selectedRows, props.items)
    || selectedRows.length === props.maximumLearnersSelected;

  if (isIncludesAll) {
    isSelectAllRow.value = true;
    return;
  }
  isSelectAllRow.value = false;
}

/**
 * Handle select all row.
 * @param {(value: boolean) => void} selectAll Select all callback.
 * @param {boolean} allSelected Whether all currently selectable rows are already selected.
 */
function onSelectAllRow(selectAll, allSelected) {
  const willSelectAll = !allSelected;
  if (!willSelectAll || props.maximumLearnersSelected == null) {
    selectAll(willSelectAll);
    return;
  }
  const unselectedLearners = filterItemNotIncludedInAList(props.items, props.modelValue);
  const remainingCapacity = props.maximumLearnersSelected - props.modelValue.length;
  const newSelectedLearners = [
    ...props.modelValue,
    ...unselectedLearners.slice(0, Math.max(remainingCapacity, 0)),
  ];
  emits('update:model-value', newSelectedLearners);
  toggleSelectAllCheckbox(newSelectedLearners);
}

/**
 * Remove learner.
 * @param {Learner} learner Learner.
 */
function onRemoveClick(learner) {
  emits('on-remove', learner);
}

/**
 * Handle action click
 * @param {Learner} learner Learner.
 */
async function onActionClick(learner) {
  emits('on-action', learner);
}

/**
 * On select learners
 * @param {Learner[]} learners Learners
 */
function onSelectRow(learners) {
  emits('update:model-value', learners);
  toggleSelectAllCheckbox(learners);
}

/**
 * Is learner selectable.
 * @param {Learner} learner Learner.
 * @returns {boolean}
 */
function isItemSelectable(learner) {
  if (props.disableHiddenLearners && !learner.isVisible) {
    return false;
  }
  const isLearnerSelected = props.modelValue.find(l => l.id === learner.id) != null;
  if (isLearnerSelected) {
    return true;
  }
  if (props.maximumLearnersSelected == null) {
    return true;
  }
  return props.maximumLearnersSelected > props.modelValue.length;
}

watch(() => props.items, () => {
  const isIncludesAll = checkIfLearnerListIncludeAnotherList(props.modelValue, props.items);
  if (isIncludesAll) {
    isSelectAllRow.value = true;
    return;
  }

  isSelectAllRow.value = false;
});

</script>

<template>
  <!-- Use v-data-table-server because scrolling is laggy when using v-data-table-virtual -->
  <v-data-table-server
    :model-value="props.modelValue"
    :headers="headers"
    :items="items"
    :loading="isLoading"
    return-object
    :item-selectable="isItemSelectable"
    :items-length="items.length"
    :select-strategy="selectStrategy"
    :show-select="showSelect"
    fixed-header
    sort-asc-icon="fas fa-sort-up"
    sort-desc-icon="fas fa-sort-down"
    :no-data-text="globalTranslate('table.noDataText')"
    :class="['app-table', props.shouldApplyClass ? $style.unnasignedLearnerTable : undefined]"
    @update:model-value="onSelectRow"
  >
    <template #headers="{ columns, isSorted, getSortIcon, toggleSort,someSelected,allSelected,selectAll,sortBy }">
      <tr>
        <td v-if="showSelect" :class="[$style.checkboxHeader, $style.header]">
          <v-checkbox-btn
            v-if="selectStrategy !== 'single'"
            v-model="isSelectAllRow"
            :density="isMobile ? 'compact' : 'default'"
            :indeterminate="someSelected && !allSelected"
            @click="onSelectAllRow(selectAll, allSelected)"
          />
        </td>
        <template v-for="column in columns" :key="column.key">
          <td
            v-if="column.key !== SELECT_ROW_COLUMN_KEY"
            :class="$style.header"
            :style="{width: `${column.width}px`, minWidth: `${column.minWidth}px`, maxWidth: `${column.maxWidth}px`}"
          >
            <div :class="[column.sortable && $style.pointerCursor]">
              <span
                :style="{ textAlign: column.align ?? 'left' }"
                @click="
                  () => {
                    if (column.sortable) {
                      toggleSort(column);
                    }
                  }
                "
              >{{ column.title }}
                <v-icon
                  v-if="isSorted(column)"
                  :class="$style.sortIcon"
                  :size="16"
                  :icon="getSortIcon(column)"
                />
                <v-icon
                  v-if="column.sortable && sortBy.length === 0"
                  :class="$style.sortIcon"
                  :size="16"
                  icon="fas fa-sort"
                />
              </span>
            </div>
          </td>
        </template>
      </tr>
    </template>

    <!-- Bind all props of v-data-table-server to slot -->
    <template v-for="(slot, slotName) in $slots" #[`${slotName}`]="slotProps">
      <slot :name="slotName" v-bind="slotProps" />
    </template>

    <template #item.data-table-select="{ internalItem, item, toggleSelect, isSelected }">
      <unassigned-table-checkbox
        :disabled="!internalItem.selectable"
        :model-value="isSelected({value: item, selectable: internalItem.selectable})"
        @update:model-value="($event) => toggleSelect({value: item, selectable: $event})"
      />
    </template>

    <template v-if="hasStudentViewUrl" #item.id="{ internalItem: item }">
      <router-link class="link" :to="redirectUtils.getStudentDetailUrl(authStore.currentUser, item.raw, '')">
        {{ item.raw.id }}
      </router-link>
    </template>

    <template #item.assign="{ internalItem: item }">
      <button
        class="text"
        type="button"
        @click="onActionClick(item.raw)"
      >
        {{ actionButtonTitle }}
      </button>
    </template>

    <template #item.status="{ internalItem: item }">
      <p>{{ getUserReadableStatus(item.raw) }}</p>
    </template>

    <template #item.delete="{ internalItem: item }">
      <button
        :title="globalTranslate('unassignedLearnersTable.deleteButton')"
        type="button"
        @click="onRemoveClick(item.raw)"
      >
        <v-icon size="small" icon="fas fa-solid fa-trash v-icon_secondary" />
      </button>
    </template>

    <!-- To remove default pagination footer -->
    <template #bottom />
  </v-data-table-server>
</template>

<style module lang="scss">

.checkboxHeader {

  /* To overwrite default padding of vue table header. */
  padding-left: 8px !important
}

.header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  width: fit-content;
  background: white;
  font-weight: 600;
}

.pointerCursor {
  cursor: pointer;
}

.sortIcon {
  margin-bottom: 4px;
}

.unnasignedLearnerTable {
    height: calc(100vh - 505px);
}

@media screen and (max-width: 1366px) {
  .unnasignedLearnerTable {
    height: calc(100vh - 565px);
  }
}

@media screen and (max-width: 600px) {
  .unnasignedLearnerTable {
    min-height: 375px;
  }
}

@media screen and (max-height: 650px) {
  .assignedCoursesTable {
    min-height: 375px;
  }
}

</style>
